import { ReactNode, useContext, useEffect, useMemo, useState } from "react"
import { Box, Stack, Typography } from "@mui/material";
import { SelectionContext } from "context";
import { FileExplorerHeader, FileExplorerView } from "components/FileExplorerHeader";
import { FileExplorer } from "components/FileExplorer";
import SimpleModal from "components/@beca-common-react-legacy/SimpleModal";
import { useDownloadFile, useCreatedFiles } from "hooks";
import { Fullscreen } from "components/Fullscreen";
import { SimpleCarousel } from "components/SimpleCarousel";
import { FileViewLoader } from "components/FileView";
import { FileEdit } from "components/FileEdit";
import { last } from "lodash";
import { Document } from "types";
import { useParams } from "react-router-dom";
import { useView } from "hooks/queries/useView";
import { useDocuments } from "hooks/queries/useDocuments";
import spaRoutes from "../useSpaTenantRoute";
import { usePermission } from "utils/beyonAuth/usePermission";
import { PermissionWrapper } from "utils/beyonAuth/PermissionWrapper";
import { useTags } from "hooks/queries/useTags";
import { useWorkspace } from "hooks/queries/useWorkspace";
import { getContextVariables } from "utils/getContextVariables";
import { DocumentDelete } from "components/DocumentDelete";

export default function Documents() {
    const { solutionId, workspaceId, viewId } = useParams<{ solutionId: string, workspaceId: string, viewId: string }>();
    const { data: viewConfig } = useView(workspaceId, viewId);
    const workspaceConfig = useWorkspace(workspaceId)?.data?.config;

    const { selection, selectionIds, setSelection, selectNext, selectPrevious } = useContext(SelectionContext);
    const [isFilterSelected, setIsFilterSelected] = useState(false);
    const { data: tags } = useTags({
        workspaceId,
        workspaceConfig,
        enabled: !!workspaceConfig?.tagConfig && !!viewConfig?.gridConfig?.enableTags
    });

    const { documents, isLoading, count, pageSize, pageIndex, sortOrder, refetch, handleTagFilterChange, handlePageChange, handleFilterChange, handleSortChange, handleSelectionFilterChange } = useDocuments(workspaceId, viewId);

    const { downloadFiles } = useDownloadFile(workspaceId);

    //todo cleanup these states
    const [displayView, setDisplayView] = useState<FileExplorerView>("table");
    const [displayIconSize, setDisplayIconSize] = useState<number>(150);
    const [showFullscreen, setFullscreen] = useState(false);
    const [modal, setModal] = useState<{ body: ReactNode, title: string, onSuccess?: () => void }>();

    const lastSelection = useMemo(() => last(selection), [selection]);

    const canEditData = usePermission("EditResourceData", workspaceId);
    const canEditConfig = usePermission("EditResourceConfiguration", workspaceId);
    const configUrl = `https://admincenter.beyon.global/solution/${solutionId}/workspace/${workspaceId}/config/content/view/${viewId}`;

    useCreatedFiles({
        onNewFiles: () => {
            refetch();
        }
    });

    return (
        <PermissionWrapper permission="ReadResourceData" workspaceId={workspaceId} fallback>
            <SimpleModal open={!!modal} title={modal?.title} onClose={() => setModal(null)}>
                {modal?.body}
            </SimpleModal>
            <Fullscreen show={!!showFullscreen} onClose={() => setFullscreen(false)}>
                <SimpleCarousel
                    items={documents}
                    item={lastSelection}
                    onNext={selectNext}
                    onPrevious={selectPrevious}
                    itemComponent={(item) => <FileViewLoader
                        document={item}
                        fileView="original"
                        onClose={() => setFullscreen(false)}
                        allowZoomPanPinch
                        fallbackMessage />
                    }
                />
            </Fullscreen>
            <Stack height="100vh">
                <Box p={1} pb={0}>
                    <FileExplorerHeader
                        view={displayView}
                        tags={tags}
                        onViewChange={setDisplayView}
                        iconSize={displayIconSize}
                        onIconSizeChange={setDisplayIconSize}
                        selection={selection}
                        isFilterBySelection={isFilterSelected}
                        onFilterBySelection={handleToggle}
                        upload={canEditData && viewConfig?.enableUpload}
                        edit={canEditData && viewConfig?.enableEdit}
                        deletion={canEditData && viewConfig?.enableDelete}
                        canEditConfig={canEditConfig}
                        download={viewConfig?.enableDownload}
                        onDelete={onDelete}
                        onUpload={() => openWindow()}
                        onEdit={onEdit}
                        onDownload={onDownload}
                        onTagFilter={handleTagFilterChange}
                        configUrl={configUrl}
                    />
                </Box>
                <Box height={`100%`} p={1} display="flex" >
                    <FileExplorer
                        documents={documents}
                        documentsCount={count}
                        workspaceConfig={workspaceConfig}
                        properties={workspaceConfig?.propertiesSchema?.filter(x => viewConfig?.gridConfig?.properties.includes(x.name))}
                        pageSize={pageSize}
                        pageIndex={pageIndex}
                        sortOrder={sortOrder}
                        loading={isLoading}
                        preview={viewConfig?.enablePreview && selection.length > 0}
                        displayView={displayView}
                        tileSize={displayIconSize}
                        selection={selection}
                        enableTags={workspaceConfig?.tagConfig && viewConfig?.gridConfig.enableTags}
                        onSelection={setSelection}
                        onNameClick={onFileNameClick}
                        onPageChange={handlePageChange}
                        onFilterChange={handleFilterChange}
                        onSortChange={handleSortChange}
                        onPreviewClick={() => setFullscreen(true)} />
                </Box>
            </Stack>
        </PermissionWrapper>
    );

    function onFileNameClick(file: Document) {
        setSelection([file]);
        setFullscreen(true);
    }


    function onEdit() {
        setModal({
            title: "Edit document",
            body: <FileEdit documentIds={selection.map(x => x.id)} onSuccess={onSuccess} onCancel={() => setModal(null)} />,
            onSuccess
        })

        function onSuccess() {
            setModal(null);
            refetch();
        }
    }

    function onDelete() {
        setModal({
            title: "Delete document(s)",
            body: <DocumentDelete documents={selection} onSuccess={onSuccess} onCancel={() => setModal(null)} />,
        });

        function onSuccess() {
            setModal(null);
            refetch();
        }
    }

    function openWindow() {
        const path = spaRoutes.upload(solutionId, workspaceId, viewId);
        const uri = new URL(`${window.location.origin}${path}`);

        const contextVariables = getContextVariables();
        Object.keys(contextVariables).forEach(x => {
            uri.searchParams.append(`context[${x}]`, contextVariables[x]);
        });

        const myRef = window.open(uri.href, '_blank', 'left=20,top=20,width=500,height=650,toolbar=1,resizable=0');
        myRef?.focus();

        if (myRef) {
            myRef.onbeforeunload = () => {
                refetch();
            }
        }
    }

    function onDownload() {
        downloadFiles({ fileIds: selection.map(x => x.fileId) });
    }

    function handleToggle() {
        return setIsFilterSelected(prev => {
            const next = !prev
            handleSelectionFilterChange(next ? selectionIds : [])
            return next
        });
    }
}

